import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUIContent, AXNUIContentCommonAsyncModal } from "@iddataweb/idw-common-ui/types";
import { ThemingFormContentValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingFormContentAsyncModalTextsSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    validationErrors: ThemingFormContentValidationErrors;
    inheritedAXNUIContent: AXNUIContent;
    axnuiContent: AXNUIContent;
    showOnlyAsyncWaitingPageContent: boolean;
    pageLevel: PageLevelTypes;
    updateAXNUIContentCommonAsyncModalFieldValue: (key: keyof AXNUIContentCommonAsyncModal, value: string) => void;
};

export const ThemingFormContentAsyncModalTextsSection = (props: ThemingFormContentAsyncModalTextsSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        validationErrors,
        inheritedAXNUIContent,
        axnuiContent,
        showOnlyAsyncWaitingPageContent,
        pageLevel,
        updateAXNUIContentCommonAsyncModalFieldValue
    } = props;

    const handleContinueInfoEmailTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentCommonAsyncModalFieldValue("continueInfoEmail", event.target.value);
    };

    const handleResetContinueInfoEmailText = () => {
        updateAXNUIContentCommonAsyncModalFieldValue("continueInfoEmail", inheritedAXNUIContent.common.asyncModal.continueInfoEmail);
    };

    const handleContinueInfoPhoneTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentCommonAsyncModalFieldValue("continueInfoPhone", event.target.value);
    };

    const handleResetContinueInfoPhoneText = () => {
        updateAXNUIContentCommonAsyncModalFieldValue("continueInfoPhone", inheritedAXNUIContent.common.asyncModal.continueInfoPhone);
    };

    const handleDetailTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentCommonAsyncModalFieldValue("details", event.target.value);
    };

    const handleResetDetailText = () => {
        updateAXNUIContentCommonAsyncModalFieldValue("details", inheritedAXNUIContent.common.asyncModal.details);
    };

    const handleCancelButtonTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentCommonAsyncModalFieldValue("cancel", event.target.value);
    };

    const handleResetCancelButtonText = () => {
        updateAXNUIContentCommonAsyncModalFieldValue("cancel", inheritedAXNUIContent.common.asyncModal.cancel);
    };

    const handleNoRetriesLeftTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentCommonAsyncModalFieldValue("noRetriesLeft", event.target.value);
    };

    const handleResetNoRetriesLeftText = () => {
        updateAXNUIContentCommonAsyncModalFieldValue("noRetriesLeft", inheritedAXNUIContent.common.asyncModal.noRetriesLeft);
    };

    const handleAsyncSubmissionCompleteTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentCommonAsyncModalFieldValue("complete", event.target.value);
    };

    const handleResetAsyncSubmissionCompleteText = () => {
        updateAXNUIContentCommonAsyncModalFieldValue("complete", inheritedAXNUIContent.common.asyncModal.complete);
    };

    const handleAsyncSubmissionInProgressTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentCommonAsyncModalFieldValue("waiting", event.target.value);
    };

    const handleResetAsyncSubmissionInProgressText = () => {
        updateAXNUIContentCommonAsyncModalFieldValue("waiting", inheritedAXNUIContent.common.asyncModal.waiting);
    };

    return (
        <div>
            {!showOnlyAsyncWaitingPageContent && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.common.asyncModal.continueInfoEmail}
                        currentThemingPropertyValue={axnuiContent.common.asyncModal.continueInfoEmail}
                        pageLevel={pageLevel}
                    >
                        <TextareaFormGroup
                            controlId={"theming-form-content-continue-info-email-input"}
                            automationId={"theming-form-content-continue-info-email-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.continueInfoEmailText.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.continueInfoEmailText.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleContinueInfoEmailTextChange}
                            value={axnuiContent.common.asyncModal.continueInfoEmail || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.continueInfoEmailText.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetContinueInfoEmailText}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.continueInfoEmailTextError}
                </>
            )}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.common.asyncModal.continueInfoPhone}
                currentThemingPropertyValue={axnuiContent.common.asyncModal.continueInfoPhone}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"theming-form-content-continue-info-phone-input"}
                    automationId={"theming-form-content-continue-info-phone-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.continueInfoPhoneText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.continueInfoPhoneText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleContinueInfoPhoneTextChange}
                    value={axnuiContent.common.asyncModal.continueInfoPhone || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.continueInfoPhoneText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetContinueInfoPhoneText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.continueInfoPhoneTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.common.asyncModal.details}
                currentThemingPropertyValue={axnuiContent.common.asyncModal.details}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"theming-form-content-detail-text-input"}
                    automationId={"theming-form-content-detail-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.detailText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.detailText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDetailTextChange}
                    value={axnuiContent.common.asyncModal.details || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.detailText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDetailText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.detailTextError}
            {!showOnlyAsyncWaitingPageContent && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.common.asyncModal.cancel}
                        currentThemingPropertyValue={axnuiContent.common.asyncModal.cancel}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-form-content-cancel-button-text-input"}
                            automationId={"theming-form-content-cancel-button-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.cancelButtonText.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.cancelButtonText.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleCancelButtonTextChange}
                            value={axnuiContent.common.asyncModal.cancel || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.cancelButtonText.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetCancelButtonText}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.cancelButtonTextError}
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.common.asyncModal.noRetriesLeft}
                        currentThemingPropertyValue={axnuiContent.common.asyncModal.noRetriesLeft}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-form-content-no-retries-left-text-input"}
                            automationId={"theming-form-content-no-retries-left-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.noRetriesLeftText.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.noRetriesLeftText.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleNoRetriesLeftTextChange}
                            value={axnuiContent.common.asyncModal.noRetriesLeft || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.noRetriesLeftText.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetNoRetriesLeftText}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.noRetriesLeftTextError}
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.common.asyncModal.complete}
                        currentThemingPropertyValue={axnuiContent.common.asyncModal.complete}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-form-content-async-submission-complete-text-input"}
                            automationId={"theming-form-content-async-submission-complete-text-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.asyncSubmissionCompleteText.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.asyncSubmissionCompleteText.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleAsyncSubmissionCompleteTextChange}
                            value={axnuiContent.common.asyncModal.complete || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.asyncSubmissionCompleteText.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetAsyncSubmissionCompleteText}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.asyncSubmissionCompleteTextError}
                </>
            )}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.common.asyncModal.waiting}
                currentThemingPropertyValue={axnuiContent.common.asyncModal.waiting}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-form-content-async-submission-in-progress-text-input"}
                    automationId={"theming-form-content-async-submission-in-progress-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.asyncSubmissionInProgressText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.asyncSubmissionInProgressText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAsyncSubmissionInProgressTextChange}
                    value={axnuiContent.common.asyncModal.waiting || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.asyncModalTexts.inputs.asyncSubmissionInProgressText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAsyncSubmissionInProgressText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.asyncSubmissionInProgressTextError}
        </div>
    );
};